import React from "react";
import AnimationRevealPage from "../../../../helpers/AnimationRevealPage.js";
import tw from "twin.macro";
import Header from "../../../../Components/headers/PageHeader";
import MainFeature1 from "../../../../Components/Features/TwoColWithButton.js";
import Features from "../../../../Components/Features/ThreeColumnPages";
import {
  FaVideo,
  FaEye,
  FaRocket,
  FaMobileAlt,
  FaStar,
  FaPaintBrush,
} from "react-icons/fa";

// Styled Components
const Subheading = tw.span`uppercase tracking-wider text-sm`;
const Heading = tw.h1`text-2xl md:text-4xl lg:text-5xl font-bold`;
const Description = tw.p`text-base md:text-lg lg:text-xl`;

export const AnimatedVideoProductionInAlBarsha = () => {
  return (
    <AnimationRevealPage>
      {/* Header Section */}
      <Header from="Animated Video Production in Al Barsha" />

      {/* Hero Section */}
      <MainFeature1
        subheading={<Subheading>Bring Ideas to Life</Subheading>}
        heading={
          <Heading>
            Professional{" "}
            <span tw="text-primary-500">Animated Video Production</span> in Al
            Barsha
          </Heading>
        }
        description={
          <Description>
            At MilaajBrandset, our animated video production services in **Al
            Barsha** create engaging, visually stunning animations for brands,
            education, and entertainment. From 2D explainer videos to stop
            motion storytelling, we craft animations that captivate and
            communicate your message effectively.
          </Description>
        }
        imageSrcs={[
          "https://images.unsplash.com/photo-1633412802994-5c058f151b66?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80", // Animation workspace
          "https://images.unsplash.com/photo-1618004912476-29818d81ae2e?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80", // 3D animation concept
          "https://images.unsplash.com/photo-1611162617213-7d7a39e9b1d7?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80", // Digital artwork
        ]}
        widths={["55%", "60%", "70%"]}
        heights={["45%", "60%", "35%"]}
        imageColumnStyle="height: 400px;"
        imageStyle="width: 45%; height: 60%;"
        imagePositions={[
          "left: 35%; top: 20%;",
          "right: 30%; top: 10%;",
          "left: 32%; bottom: 20%;",
        ]}
        primaryButtonText="Start Your Animation"
        buttonRounded={true}
        textOnLeft={false}
      />

      {/* Key Benefits Section */}
      <Features
        subheading={<Subheading>Why Choose Us?</Subheading>}
        heading="Expert Animated Video Production in Al Barsha"
        description="We create animations that engage, inform, and inspire."
        cards={[
          {
            icon: <FaPaintBrush color="#ff5464" size={40} />,
            title: "Creative Animations",
            description:
              "Unique 2D, 3D, or stop motion styles tailored to your vision.",
          },
          {
            icon: <FaEye color="#ff5464" size={40} />,
            title: "Engaging Visuals",
            description: "Vibrant designs that capture audience attention.",
          },
          {
            icon: <FaRocket color="#ff5464" size={40} />,
            title: "Fast Turnaround",
            description: "Timely delivery to meet your project deadlines.",
          },
          {
            icon: <FaMobileAlt color="#ff5464" size={40} />,
            title: "Platform-Optimized",
            description:
              "Animations suited for social media, websites, or campaigns.",
          },
          {
            icon: <FaStar color="#ff5464" size={40} />,
            title: "Trusted Expertise",
            description: "Al Barsha’s choice for professional animation.",
          },
          {
            icon: <FaVideo color="#ff5464" size={40} />,
            title: "End-to-End Service",
            description: "From concept to final render, we handle it all.",
          },
        ]}
      />

      {/* Our Process Section */}
      <MainFeature1
        subheading={<Subheading>How We Work</Subheading>}
        heading="Our Animated Video Production Process"
        description={
          <Description>
            We craft impactful animations through a streamlined, creative
            process:
            <ul tw="mt-4 list-disc pl-6">
              <li>
                Consultation: Understand your goals and animation style
                preferences.
              </li>
              <li>
                Storyboarding: Develop a visual script to outline the narrative.
              </li>
              <li>
                Design: Create characters, assets, or scenes in 2D, 3D, or stop
                motion.
              </li>
              <li>
                Animation: Bring designs to life with smooth, engaging motion.
              </li>
              <li>Post-Production: Add sound, effects, and final polish.</li>
              <li>
                Delivery: Provide a high-quality video ready for your platforms.
              </li>
            </ul>
          </Description>
        }
        imageSrcs={[
          "https://images.unsplash.com/photo-1628348070889-cb656235b4eb?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80", // Digital illustration
          "https://images.unsplash.com/photo-1633412802994-5c058f151b66?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80", // Animation workspace
          "https://images.unsplash.com/photo-1635070041078-e363dbe005cb?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80", // 3D modeling screen
        ]}
        widths={["60%", "55%", "70%"]}
        heights={["60%", "45%", "35%"]}
        imageColumnStyle="height: 400px;"
        imageStyle="width: 45%; height: 60%;"
        imagePositions={[
          "right: 30%; top: 10%;",
          "left: 35%; bottom: 20%;",
          "right: 32%; bottom: 10%;",
        ]}
        primaryButtonText="Learn More"
        buttonRounded={true}
        textOnLeft={true}
      />

      {/* Success Stories Section */}
      <Features
        subheading={<Subheading>Success Stories</Subheading>}
        heading="Animation Wins in Al Barsha"
        description="Our animated videos have driven engagement and impact."
        cards={[
          {
            icon: <FaVideo color="#ff5464" size={40} />,
            title: "Viral Explainer",
            description: "Created a 2D video that boosted brand awareness.",
          },
          {
            icon: <FaEye color="#ff5464" size={40} />,
            title: "Educational Impact",
            description:
              "Produced a stop motion series for e-learning success.",
          },
          {
            icon: <FaStar color="#ff5464" size={40} />,
            title: "Client Satisfaction",
            description: "96% approval from animation clients.",
          },
        ]}
      />

      {/* Call-to-Action Section */}
      <MainFeature1
        subheading={<Subheading>Ready for Stunning Animations?</Subheading>}
        heading="Animated Video Production in Al Barsha"
        description={
          <Description>
            Partner with MilaajBrandset for professional animated video
            production in Al Barsha. Create engaging, high-quality animations
            that captivate and convey your message—contact us today!
          </Description>
        }
        imageSrcs={[
          "https://images.unsplash.com/photo-1648387598225-1a44ef324bee?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80", // Creative design workspace
          "https://images.unsplash.com/photo-1579403124614-197f69d8187b?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80", // Digital animation concept
          "https://images.unsplash.com/photo-1626785774625-6babb4418d56?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80", // Animation monitor setup
        ]}
        widths={["55%", "60%", "70%"]}
        heights={["45%", "60%", "35%"]}
        imageColumnStyle="height: 400px;"
        imageStyle="width: 45%; height: 60%;"
        imagePositions={[
          "left: 35%; top: 20%;",
          "right: 30%; top: 10%;",
          "left: 32%; bottom: 20%;",
        ]}
        primaryButtonText="Contact Us Now"
        buttonRounded={true}
        textOnLeft={false}
      />
    </AnimationRevealPage>
  );
};
